// Intent: the owner's "Wspólne mieszkanko" sheet — fixed monthly costs with
// SNAPSHOT COLUMNS showing how fees grew over time (czynsz 800→830…). Model:
// each cost keeps its amount history; editing the amount archives the old one.
// Confirming "zapłacone" for a month adds a BILLS expense to the budget
// (owner decision) and remembers the month. Local JSON-doc, synced via TABLES.

import { create } from 'zustand';

import { getDb, TABLES } from '@/db';
import { cancelFixedCostPrompt, scheduleFixedCostPrompt } from '@/notifications/local';
import { scheduleHouseholdSync } from '@/sync/household-sync';
import { useBudgetStore } from '@/store/budget-store';
import { medianDayOf } from '@/store/income-store';
import type { FixedCost, Reminder } from '@/types/domain';
import { formatPln, uuid } from '@/utils/format';

export interface NewFixedCostInput {
  name: string;
  amountGr: number;
  dueDay: number | null;
  addToBudget?: boolean;
}

/** Total of active fixed costs (current amounts). */
export function fixedCostsTotalGrOf(costs: FixedCost[]): number {
  return costs.filter((c) => c.active).reduce((s, c) => s + c.amountGr, 0);
}

/**
 * Virtual calendar items: the NEXT occurrence of each active cost as a
 * Reminder-shaped row (type FIXED_COST) — the reminders screen shows them
 * without persisting monthly rows. No dueDay -> the 1st of the month
 * (owner decision: "jak nie ma daty to na początku miesiąca").
 */
export function fixedCostRemindersOf(costs: FixedCost[], now = new Date()): Reminder[] {
  return costs
    .filter((c) => c.active)
    .map((c) => {
      const day = c.dueDay ?? 1;
      let d = new Date(now.getFullYear(), now.getMonth(), Math.min(day, 28));
      if (d.getDate() < now.getDate()) d = new Date(now.getFullYear(), now.getMonth() + 1, Math.min(day, 28));
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return {
        id: `fixedcost-${c.id}`,
        policyId: null,
        type: 'FIXED_COST' as const,
        title: c.name,
        subtitle: formatPln(c.amountGr),
        eventDate: iso,
        leadDaysBefore: [],
        enabled: true,
        lastFiredAt: null,
        updatedAt: c.updatedAt,
      };
    });
}

/**
 * Sum-over-time points for the growth chart: every date any amount changed,
 * with the total of all (then-active) costs at that date. Uses history and
 * treats each cost's current amount as valid from its last change.
 */
export function totalHistoryOf(costs: FixedCost[]): { label: string; valueGr: number }[] {
  const active = costs.filter((c) => c.active);
  const dates = new Set<string>();
  for (const c of active) {
    dates.add(c.createdAt.slice(0, 10));
    for (const h of c.history) dates.add(h.fromIso.slice(0, 10));
  }
  const sorted = [...dates].sort();
  const amountAt = (c: FixedCost, iso: string): number => {
    // history holds PREVIOUS amounts with the date each STARTED applying;
    // the current amountGr applies from the last history entry's end.
    let amount = c.history.length ? c.history[0].amountGr : c.amountGr;
    for (const h of c.history) if (h.fromIso.slice(0, 10) <= iso) amount = h.amountGr;
    const lastChange = c.history.length ? c.history[c.history.length - 1] : null;
    if (!lastChange || iso >= c.updatedAt.slice(0, 10)) amount = c.amountGr;
    return c.createdAt.slice(0, 10) <= iso ? amount : 0;
  };
  const points = sorted.map((iso) => ({
    label: `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}`,
    valueGr: active.reduce((s, c) => s + amountAt(c, iso), 0),
  }));
  // Collapse to at most 6 points (first, evenly spaced, last) — phone-first.
  if (points.length > 6) {
    const keep = [0];
    for (let i = 1; i < 5; i++) keep.push(Math.round((i * (points.length - 1)) / 5));
    keep.push(points.length - 1);
    return [...new Set(keep)].map((i) => points[i]);
  }
  return points;
}

/**
 * "Czynsz potwierdzasz zwykle ok. 12., nie 10. — przestawić?" — proposes a new
 * dueDay when the confirmation dates (paidDates, honest signal: when the user
 * ticked "zapłacone", not when the bank moved money) drift ≥2 days over the
 * last 3 confirmations. Dismissed day never returns; AI proposes, human clicks.
 */
export function costDaySuggestionOf(cost: FixedCost): number | null {
  if (!cost.active || cost.dueDay === null) return null;
  const days = Object.entries(cost.paidDates ?? {})
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([, iso]) => Number(iso.slice(8, 10)));
  const median = medianDayOf(days);
  if (median === null || median < 1 || median > 28) return null;
  if (Math.abs(median - cost.dueDay) < 2) return null;
  if (cost.daySuggestionDismissed === median) return null;
  return median;
}

interface FixedCostsState {
  hydrated: boolean;
  costs: FixedCost[];
  hydrate: () => Promise<void>;
  addCost: (input: NewFixedCostInput) => Promise<void>;
  /** Changing amount archives the old one into history (the Excel snapshots). */
  updateCost: (id: string, patch: Partial<NewFixedCostInput>) => Promise<void>;
  removeCost: (id: string) => Promise<void>;
  /** Mark month 'YYYY-MM' paid; adds a BILLS budget expense when addToBudget. */
  markPaid: (id: string, month: string) => Promise<void>;
  unmarkPaid: (id: string, month: string) => Promise<void>;
  /** "Tej opłaty nie było w tym miesiącu" — closes the question for that month. */
  skipMonth: (id: string, month: string) => Promise<void>;
  unskipMonth: (id: string, month: string) => Promise<void>;
  /** "Zostaw" on a day-change suggestion — that day is never proposed again. */
  dismissDaySuggestion: (id: string, day: number) => Promise<void>;
}

async function persist(cost: FixedCost): Promise<void> {
  const db = await getDb();
  await db.upsert(TABLES.fixedCosts, cost.id, cost);
}

export const useFixedCostsStore = create<FixedCostsState>((set, get) => ({
  hydrated: false,
  costs: [],

  hydrate: async () => {
    const db = await getDb();
    const costs = (await db.list<FixedCost>(TABLES.fixedCosts)).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    set({ hydrated: true, costs });
  },

  addCost: async (input) => {
    const now = new Date().toISOString();
    const cost: FixedCost = {
      id: uuid(),
      name: input.name.trim(),
      amountGr: input.amountGr,
      dueDay: input.dueDay,
      history: [],
      addToBudget: input.addToBudget ?? true,
      paidMonths: [],
      active: true,
      createdAt: now,
      updatedAt: now,
    };
    await persist(cost);
    set((s) => ({ costs: [...s.costs, cost] }));
    void scheduleFixedCostPrompt(cost.id, cost.name, formatPln(cost.amountGr), cost.dueDay).catch(
      () => undefined,
    );
    scheduleHouseholdSync();
  },

  updateCost: async (id, patch) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current) return;
    const now = new Date().toISOString();
    const amountChanged = patch.amountGr !== undefined && patch.amountGr !== current.amountGr;
    const updated: FixedCost = {
      ...current,
      ...patch,
      // Archive the outgoing amount so the growth chart keeps the snapshot.
      history: amountChanged
        ? [...current.history, { fromIso: current.updatedAt, amountGr: current.amountGr }]
        : current.history,
      updatedAt: now,
    };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
    void scheduleFixedCostPrompt(updated.id, updated.name, formatPln(updated.amountGr), updated.dueDay).catch(
      () => undefined,
    );
    scheduleHouseholdSync();
  },

  removeCost: async (id) => {
    const db = await getDb();
    await db.remove(TABLES.fixedCosts, id);
    set((s) => ({ costs: s.costs.filter((c) => c.id !== id) }));
    void cancelFixedCostPrompt(id);
  },

  markPaid: async (id, month) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current || current.paidMonths.includes(month)) return;
    const nowIso = new Date().toISOString();
    const updated: FixedCost = {
      ...current,
      paidMonths: [...current.paidMonths, month],
      // Confirmation date feeds the "przestawić dzień?" suggestion (A5) —
      // paidMonths alone never said WHEN the user confirmed.
      paidDates: { ...(current.paidDates ?? {}), [month]: nowIso.slice(0, 10) },
      updatedAt: nowIso,
    };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
    scheduleHouseholdSync();
    if (current.addToBudget) {
      // Owner decision: confirming payment writes the expense into the budget.
      await useBudgetStore.getState().addEntry('BILLS', current.amountGr);
    }
  },

  unmarkPaid: async (id, month) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current) return;
    const remainingDates = { ...(current.paidDates ?? {}) };
    delete remainingDates[month];
    const updated: FixedCost = {
      ...current,
      paidMonths: current.paidMonths.filter((m) => m !== month),
      paidDates: remainingDates,
      updatedAt: new Date().toISOString(),
    };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
    // The budget entry stays — removing money that was really spent would lie.
  },

  skipMonth: async (id, month) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current || (current.skippedMonths ?? []).includes(month)) return;
    const updated: FixedCost = {
      ...current,
      skippedMonths: [...(current.skippedMonths ?? []), month],
      updatedAt: new Date().toISOString(),
    };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
    scheduleHouseholdSync();
  },

  unskipMonth: async (id, month) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current) return;
    const updated: FixedCost = {
      ...current,
      skippedMonths: (current.skippedMonths ?? []).filter((m) => m !== month),
      updatedAt: new Date().toISOString(),
    };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
    scheduleHouseholdSync();
  },

  dismissDaySuggestion: async (id, day) => {
    const current = get().costs.find((c) => c.id === id);
    if (!current) return;
    const updated: FixedCost = { ...current, daySuggestionDismissed: day, updatedAt: new Date().toISOString() };
    await persist(updated);
    set((s) => ({ costs: s.costs.map((c) => (c.id === id ? updated : c)) }));
  },
}));
